import { useState, useEffect, useMemo, useRef } from 'react';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import socket from 'websockets/socket';
import { v4 as uuidV4 } from 'uuid';
import { runSavedPromptTyped } from 'thunks/runSavedPromptTyped';

export const useStreamingNotePrompts = ({
  originalNote,
  includeSummary = false,
  includeNote = true,
}) => {
  const dispatch = useAppDispatch();
  const [streamingKey, setStreamingKey] = useState(null);
  const [streamingData, setStreamingData] = useState([]);
  const [loadingNote, setLoadingNote] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [failedSections, setFailedSections] = useState([]);

  // The listener is attached once per connection, so it must read the key of
  // the run that is live right now — chunks can arrive before React commits it.
  const streamingKeyRef = useRef(null);
  // Identifies the newest run so a superseded one cannot write its stale text.
  const runRef = useRef(0);

  const socketConnection = useAppSelector((state) => state.resource.socketConnection);

  async function promptNote(streamingKey, fields, patientId) {
    return runSavedPromptTyped('Beautify Note', fields, { patientId, streamingKey });
  }

  async function promptSummary(streamingKey, fields, patientId) {
    return runSavedPromptTyped('Note Summary', fields, { streamingKey, patientId });
  }

  function receivedNoteStreaming(data) {
    const currentKey = streamingKeyRef.current;
    // Keyless frames (heartbeats/terminators) share this event.
    if (!currentKey || !data?.streamingKey?.startsWith(currentKey)) return;
    setStreamingData((prevState) =>
      // index 0 means the backend restarted that section's stream.
      data.index === 0
        ? [...prevState.filter((chunk) => chunk.streamingKey !== data.streamingKey), data]
        : [...prevState, data],
    );
  }

  const filteredStreamingText = useMemo(() => {
    let result = {};
    streamingData.forEach((data) => {
      let key = data.streamingKey.replace(streamingKey, '');
      result[key] = result[key] || '';
      result[key] += data.text;
    });
    return [
      result.Summary,
      result.S,
      result.O,
      result.A,
      result.P,
      includeNote ? undefined : originalNote,
    ]
      .filter((value) => value)
      .join('\n\n');
  }, [streamingData, streamingKey, includeNote, originalNote]);

  useEffect(() => {
    const key = 'ai-streaming';
    let instance = socket.getSocket();
    if (socketConnection !== 'connected' || !instance) return undefined;
    const listener = (data) => {
      receivedNoteStreaming(data);
    };
    instance.on(key, listener);
    // Removing without the handler drops *every* callback for the event —
    // other features (the Carey assistant) stream on this same event.
    return () => instance.removeListener(key, listener);
  }, [socketConnection]);

  const generateNote = async (fields, patientId) => {
    const newStreamingKey = uuidV4();
    const runId = runRef.current + 1;
    runRef.current = runId;
    streamingKeyRef.current = newStreamingKey;
    setStreamingData([]);
    setStreamingKey(newStreamingKey);
    setFailedSections([]);
    setLoadingNote(true);
    let sections;
    try {
      sections = await Promise.all([
        includeSummary && promptSummary(`${newStreamingKey}Summary`, fields, patientId),
        includeNote &&
          promptNote(
            `${newStreamingKey}S`,
            {
              ...fields,
              extraPrompt: 'Output only the S section with all the formatting rules stated above',
            },
            patientId,
          ),
        includeNote &&
          promptNote(
            `${newStreamingKey}O`,
            {
              ...fields,
              extraPrompt: 'Output only the O section with all the formatting rules stated above',
            },
            patientId,
          ),
        includeNote &&
          promptNote(
            `${newStreamingKey}A`,
            {
              ...fields,
              extraPrompt: 'Output only the A section with all the formatting rules stated above',
            },
            patientId,
          ),
        includeNote &&
          promptNote(
            `${newStreamingKey}P`,
            {
              ...fields,
              extraPrompt: 'Output only the P section with all the formatting rules stated above',
            },
            patientId,
          ),
      ]);
    } catch (error) {
      // Without this the spinner (and the disabled Accept buttons) stay stuck
      // forever on a rejected prompt.
      if (runRef.current === runId) setLoadingNote(false);
      throw error;
    }

    const [summary, s, o, a, p] = sections;
    let result = [summary, s, o, a, p].filter((v) => v).join('\n\n');
    if (!includeNote) result = [result, fields.note].filter((v) => v).join('\n\n');

    // A newer run is already streaming: it owns the note, the preview and the
    // spinner, so this one only reports its own text back to its caller.
    if (runRef.current !== runId) return result;

    setFailedSections(
      [
        ['Summary', summary],
        ['S', s],
        ['O', o],
        ['A', a],
        ['P', p],
      ]
        // A requested section that came back nullish means the call failed;
        // dropping it silently would truncate a clinical note without warning.
        .filter(([, value]) => value === undefined || value === null)
        .map(([name]) => name),
    );
    setLoadingNote(false);
    setNewNote(result);
    setStreamingData([]);

    return result;
  };

  return {
    generateNote,
    newNote,
    setNewNote,
    loadingNote,
    filteredStreamingText,
    failedSections,
  };
};
